import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Card,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const CollectionForm = () => {
  const { collectionName, id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const isEditMode = id && id !== 'new';
  const isLivesCollection = collectionName === 'lives';

  // URL fields that require validation
  const urlFields = [
    'liveaudiovideo',
    'liveaudiovideoyoutube',
    'liveaudio',
    'liveaudiovideodvrhls',
    'Liveaudiovideodvrdash'
  ];

  const fetchItem = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCollectionItem(collectionName, id);
      const item = response.data;

      if (item) {
        // Strapi v5: fields are directly on item, not in item.attributes
        // Filter out system fields
        const systemFields = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt', 'createdBy', 'updatedBy', 'locale'];
        const fieldNames = Object.keys(item).filter(
          key => !systemFields.includes(key)
        );

        // Extract only the field values (not system fields)
        const formFields = {};
        fieldNames.forEach(key => {
          formFields[key] = item[key];
        });

        setFormData(formFields);
        setFields(fieldNames);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to fetch item');
    } finally {
      setLoading(false);
    }
  }, [collectionName, id]);

  useEffect(() => {
    if (isEditMode) {
      fetchItem();
    } else {
      setFields([]);
    }
  }, [fetchItem, isEditMode]);

  const validateUrl = (url) => {
    if (!url || url.trim() === '') {
      return true; // Empty is valid (optional field)
    }
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Validate URL fields
    if (urlFields.includes(fieldName)) {
      if (!validateUrl(value)) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: 'الرجاء إدخال رابط صحيح (http:// أو https://)',
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for validation errors
    if (Object.keys(validationErrors).length > 0) {
      setError('الرجاء إصلاح جميع أخطاء التحقق قبل الحفظ.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isEditMode) {
        await apiService.updateCollectionItem(collectionName, id, formData);
        setSuccess('تم تحديث العنصر بنجاح!');
      } else {
        await apiService.createCollectionItem(collectionName, formData);
        setSuccess('تم إنشاء العنصر بنجاح!');
        setTimeout(() => {
          navigate(`/collections/${collectionName}`);
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'فشل في حفظ العنصر');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (fieldName) => {
    const value = formData[fieldName] || '';
    const fieldType = typeof value;
    const isUrlField = urlFields.includes(fieldName);
    const hasError = validationErrors[fieldName];

    // Handle different field types
    if (fieldType === 'boolean') {
      return (
        <Form.Check
          type="checkbox"
          checked={value}
          onChange={(e) => handleChange(fieldName, e.target.checked)}
        />
      );
    }

    if (fieldType === 'object' && value !== null) {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleChange(fieldName, parsed);
            } catch {
              // Invalid JSON, keep as string for now
            }
          }}
        />
      );
    }

    // Check if it's a long text field
    if (typeof value === 'string' && value.length > 100 && !isUrlField) {
      return (
        <Form.Control
          as="textarea"
          rows={5}
          value={value}
          onChange={(e) => handleChange(fieldName, e.target.value)}
        />
      );
    }

    // URL input field
    if (isUrlField) {
      return (
        <>
          <Form.Control
            type="url"
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            isInvalid={hasError}
            placeholder="https://example.com"
          />
          <Form.Control.Feedback type="invalid">
            {hasError}
          </Form.Control.Feedback>
        </>
      );
    }

    // Default text input
    return (
      <Form.Control
        type="text"
        value={value}
        onChange={(e) => handleChange(fieldName, e.target.value)}
      />
    );
  };

  // Collection names in Arabic
  const collectionNames = {
    'lives': 'البث المباشر',
    'videos': 'الفيديوهات',
    'categories': 'التصنيفات',
    'tags': 'الوسوم',
    'authors': 'الكتاب',
    'upload/files': 'الوسائط'
  };

  const collectionIcons = {
    'lives': 'bi-broadcast',
    'videos': 'bi-camera-video',
    'categories': 'bi-folder',
    'tags': 'bi-tags',
    'authors': 'bi-person-circle',
    'upload/files': 'bi-image'
  };

  // For new items without existing fields, provide a dynamic form
  const addNewField = () => {
    const fieldName = prompt('أدخل اسم الحقل:');
    if (fieldName && !fields.includes(fieldName)) {
      setFields([...fields, fieldName]);
      setFormData({ ...formData, [fieldName]: '' });
    }
  };

  if (loading) {
    const breadcrumbs = [
      { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
    ];
    return (
      <Layout title="جاري التحميل..." breadcrumbs={breadcrumbs}>
        <Container className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </Container>
      </Layout>
    );
  }

  const breadcrumbs = [
    { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
    ...(isLivesCollection
      ? [{ label: collectionNames[collectionName], icon: collectionIcons[collectionName] }]
      : [
          { label: collectionNames[collectionName], path: `/collections/${collectionName}`, icon: collectionIcons[collectionName] },
          { label: isEditMode ? 'تعديل' : 'جديد', icon: isEditMode ? 'bi-pencil' : 'bi-plus-circle' }
        ])
  ];

  const pageTitle = isLivesCollection
    ? 'إعدادات البث المباشر'
    : `${isEditMode ? 'تعديل' : 'إضافة'} ${collectionNames[collectionName] || collectionName}`;

  return (
    <Layout title={pageTitle} breadcrumbs={breadcrumbs}>
      <Container fluid>

        {error && (
          <Alert variant="danger" className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle ms-2"></i>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="d-flex align-items-center">
            <i className="bi bi-check-circle ms-2"></i>
            {success}
          </Alert>
        )}

        <Card className="shadow-sm border-0">
          <Card.Header className="bg-primary text-white">
            <i className={`${collectionIcons[collectionName]} ms-2`}></i>
            {pageTitle}
          </Card.Header>
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {fields.length === 0 && !isEditMode ? (
                <Alert variant="info" className="text-center">
                  <i className="bi bi-info-circle ms-2"></i>
                  لم يتم تحديد حقول بعد. انقر على "إضافة حقل" لبدء إضافة الحقول.
                  <div className="mt-3">
                    <Button variant="outline-primary" onClick={addNewField}>
                      <i className="bi bi-plus-circle ms-1"></i>
                      إضافة حقل
                    </Button>
                  </div>
                </Alert>
              ) : (
                <>
                  {fields.map((fieldName) => (
                    <Form.Group className="mb-3" key={fieldName}>
                      <Form.Label className="fw-bold">
                        {fieldName.replace(/([A-Z])/g, ' $1').trim()}
                      </Form.Label>
                      {renderField(fieldName)}
                    </Form.Group>
                  ))}

                  {!isEditMode && !isLivesCollection && (
                    <Button
                      variant="outline-secondary"
                      className="mb-3"
                      onClick={addNewField}
                    >
                      <i className="bi bi-plus-circle ms-1"></i>
                      إضافة حقل آخر
                    </Button>
                  )}

                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving || Object.keys(validationErrors).length > 0}
                    >
                      {saving ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="ms-2"
                          />
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <i className={`bi ${isEditMode ? 'bi-check-circle' : 'bi-plus-circle'} ms-1`}></i>
                          {isEditMode ? 'تحديث' : 'إنشاء'}
                        </>
                      )}
                    </Button>
                    {!isLivesCollection && (
                      <Button
                        variant="secondary"
                        onClick={() => navigate(`/collections/${collectionName}`)}
                      >
                        <i className="bi bi-x-circle ms-1"></i>
                        إلغاء
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default CollectionForm;
